import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaUpload, FaTimes, FaUser, FaSpinner } from 'react-icons/fa';

const TestimonialForm = ({ onSubmit, initialData, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    ministry: '',
    testimony: '',
    image: '',
    isActive: true
  });
  const [imagePreview, setImagePreview] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
      setImagePreview(initialData.image || '');
    }
  }, [initialData]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (limit to 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        alert(`Image is too large. Please select an image smaller than 5MB. Current size: ${(file.size / (1024 * 1024)).toFixed(1)}MB`);
        return;
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file.');
        return;
      }

      // Compress and resize the image
      setIsCompressing(true);
      compressImage(file, maxSize).then(compressedImage => {
        setImagePreview(compressedImage);
        setFormData(prev => ({ ...prev, image: compressedImage }));
        setIsCompressing(false);
      }).catch(error => {
        console.error('Error compressing image:', error);
        alert('Error processing image. Please try again.');
        setIsCompressing(false);
      });
    }
  };

  const compressImage = (file, maxSize) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions (max 800x800 for better quality)
        const maxDimension = 800;
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxDimension) {
            height = (height * maxDimension) / width;
            width = maxDimension;
          }
        } else {
          if (height > maxDimension) {
            width = (width * maxDimension) / height;
            height = maxDimension;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        
        // Try different quality levels to get under the size limit
        let quality = 0.9; // Start with higher quality
        let dataUrl;
        
        do {
          dataUrl = canvas.toDataURL('image/jpeg', quality);
          quality -= 0.05; // Smaller quality reduction steps
        } while (dataUrl.length > maxSize && quality > 0.3);
        
        if (dataUrl.length > maxSize) {
          // If still too large, reduce dimensions further
          canvas.width = width * 0.9;
          canvas.height = height * 0.9;
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        }
        
        resolve(dataUrl);
      };
      
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };

  const removeImage = () => {
    setImagePreview('');
    setFormData(prev => ({ ...prev, image: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await onSubmit(formData);
      if (!initialData) {
        // Reset form if creating new
        setFormData({
          name: '',
          role: '',
          ministry: '',
          testimony: '',
          image: '',
          isActive: true
        });
        setImagePreview('');
      }
    } catch (error) {
      console.error('Error submitting testimonial:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const ministryOptions = [
    'Lifeline',
    'New Life Choir',
    'Life Theatre',
    'Good Life Children & Great Life Teen\'s Ministry',
    'Life Connect',
    'Life Touch',
    'Life Media',
    'Protocol and Hospitality'
  ];

  return (
    <FormContainer>
      <FormTitle>
        {initialData ? 'Edit Testimonial' : 'Add New Testimonial'}
      </FormTitle>
      
      <Form onSubmit={handleSubmit}>
        <FormRow>
          <FormGroup>
            <Label htmlFor="name">Name *</Label>
            <Input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              placeholder="Enter person's name"
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="role">Role *</Label>
            <Input
              type="text"
              id="role"
              name="role"
              value={formData.role}
              onChange={handleInputChange}
              required
              placeholder="e.g., Ministry Leader, Member"
            />
          </FormGroup>
        </FormRow>

        <FormGroup>
          <Label htmlFor="ministry">Ministry (Optional)</Label>
          <Select
            id="ministry"
            name="ministry"
            value={formData.ministry}
            onChange={handleInputChange}
          >
            <option value="">No specific ministry</option>
            <option value="General Member">General Member</option>
            {ministryOptions.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </Select>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="testimony">Testimony *</Label>
          <Textarea
            id="testimony"
            name="testimony"
            value={formData.testimony}
            onChange={handleInputChange}
            required
            rows="4"
            placeholder="Share the person's testimony..."
          />
        </FormGroup>

        <FormGroup>
          <Label>Profile Image (Optional)</Label>
          <ImageUploadContainer>
            {imagePreview ? (
              <ImagePreviewContainer>
                <ImagePreview src={imagePreview} alt="Preview" />
                <RemoveImageButton onClick={removeImage}>
                  <FaTimes />
                </RemoveImageButton>
                <ImageSizeInfo>
                  Size: {Math.round(imagePreview.length * 0.75 / 1024)}KB
                </ImageSizeInfo>
              </ImagePreviewContainer>
            ) : (
              <ImageUploadArea>
                <input
                  type="file"
                  id="image"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                />
                <label htmlFor="image">
                  <UploadIcon>
                    {isCompressing ? <FaSpinner className="fa-spin" /> : <FaUpload />}
                  </UploadIcon>
                  <UploadText>
                    {isCompressing ? 'Compressing image...' : 'Click to upload image'}
                  </UploadText>
                  <UploadSubtext>
                    {isCompressing ? 'Please wait...' : 'or drag and drop'}
                  </UploadSubtext>
                  <UploadSizeLimit>Max size: 5MB</UploadSizeLimit>
                </label>
              </ImageUploadArea>
            )}
          </ImageUploadContainer>
          <ImageNote>
            If no image is provided, a default avatar will be used. 
            Images are automatically compressed and resized to fit within 5MB for optimal quality.
          </ImageNote>
        </FormGroup>

        <FormGroup>
          <CheckboxContainer>
            <input
              type="checkbox"
              id="isActive"
              name="isActive"
              checked={formData.isActive}
              onChange={handleInputChange}
            />
            <Label htmlFor="isActive" style={{ margin: 0, cursor: 'pointer' }}>
              Active (visible on website)
            </Label>
          </CheckboxContainer>
        </FormGroup>

        <ButtonGroup>
          <SubmitButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : (initialData ? 'Update Testimonial' : 'Add Testimonial')}
          </SubmitButton>
          {onCancel && (
            <CancelButton type="button" onClick={onCancel}>
              Cancel
            </CancelButton>
          )}
        </ButtonGroup>
      </Form>
    </FormContainer>
  );
};

const FormContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const FormTitle = styled.h3`
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const Textarea = styled.textarea`
  padding: 0.75rem;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 1rem;
  resize: vertical;
  min-height: 100px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const ImageUploadContainer = styled.div`
  margin-top: 0.5rem;
`;

const ImageUploadArea = styled.div`
  border: 2px dashed #e1e5e9;
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.3s ease;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
  }

  label {
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
  }
`;

const UploadIcon = styled.div`
  font-size: 2rem;
  color: #6c757d;
  
  .fa-spin {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const UploadText = styled.div`
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
`;

const UploadSubtext = styled.div`
  font-size: 0.9rem;
  color: #6c757d;
`;

const UploadSizeLimit = styled.div`
  font-size: 0.8rem;
  color: #dc3545;
  font-weight: 600;
  margin-top: 0.5rem;
`;

const ImagePreviewContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const ImagePreview = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid ${props => props.theme.colors.primary};
`;

const ImageSizeInfo = styled.div`
  font-size: 0.8rem;
  color: #6c757d;
  text-align: center;
  margin-top: 0.5rem;
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #dc3545;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  transition: background-color 0.3s ease;

  &:hover {
    background: #c82333;
  }
`;

const ImageNote = styled.p`
  font-size: 0.8rem;
  color: #6c757d;
  margin-top: 0.5rem;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    accent-color: ${props => props.theme.colors.primary};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SubmitButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.secondary};
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  background: transparent;
  color: ${props => props.theme.colors.text.secondary};
  padding: 0.75rem 1.5rem;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${props => props.theme.colors.text.primary};
    color: ${props => props.theme.colors.text.primary};
  }
`;

export default TestimonialForm;
